import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import templateProduct from '../data/templateProduct';
import { fetchProduct } from '../services/api';
import { useCart } from '../context/CartContext';

const defaultSizes = ['XS', 'S', 'M', 'L', 'XL', 'XXL'];

export default function ProductDetail() {
  const { productId } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [size, setSize] = useState(defaultSizes[0]);
  const [quantity, setQuantity] = useState(1);
  const [message, setMessage] = useState('');
  const [visualIndex, setVisualIndex] = useState(0);
  const { addItem } = useCart();

  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    setError('');
    setMessage('');
    setQuantity(1);
    setVisualIndex(0);

    const loadProduct = async () => {
      if (productId === templateProduct._id) {
        if (isMounted) {
          setProduct(templateProduct);
          setSize(defaultSizes[0]);
          setVisualIndex(0);
          setLoading(false);
        }
        return;
      }
      try {
        const data = await fetchProduct(productId);
        if (!isMounted) return;
        setProduct(data);
        if (data?.sizes?.length) {
          setSize(data.sizes[0].label);
        } else {
          setSize(defaultSizes[0]);
        }
        setVisualIndex(0);
      } catch (err) {
        if (isMounted) {
          setError(err.message || 'Produit introuvable.');
          setProduct(null);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadProduct();
    return () => {
      isMounted = false;
    };
  }, [productId]);

  const availableSizes = useMemo(() => {
    if (product?.sizes?.length) {
      return product.sizes.map((item) => item.label);
    }
    return defaultSizes;
  }, [product]);

  const visuals = useMemo(() => {
    if (product?.visuals?.length) {
      return product.visuals;
    }
    if (product?.image) {
      return [product.image];
    }
    return [templateProduct.image];
  }, [product]);
  const price = Number(product?.price ?? templateProduct.price).toFixed(2);
  const safeIndex = Math.min(visualIndex, Math.max(0, visuals.length - 1));
  const currentVisual = visuals[safeIndex] || templateProduct.image;

  useEffect(() => {
    if (!visuals.length) {
      setVisualIndex(0);
    } else if (visualIndex > visuals.length - 1) {
      setVisualIndex(0);
    }
  }, [visuals, visualIndex]);

  const handlePrevVisual = () => {
    setVisualIndex((prev) => (visuals.length ? (prev - 1 + visuals.length) % visuals.length : 0));
  };

  const handleNextVisual = () => {
    setVisualIndex((prev) => (visuals.length ? (prev + 1) % visuals.length : 0));
  };

  const handleAddToCart = () => {
    if (!product) return;
    setMessage('');
    if (product._id === templateProduct._id) {
      setMessage('Ce produit est un modèle de démonstration. Ajoutez-le depuis le back-office pour l’ouvrir aux commandes.');
      return;
    }
    const priceNumber = Number(product.price ?? templateProduct.price) || 0;
    addItem({
      productId: product._id,
      name: product.name,
      size,
      quantity,
      price: priceNumber,
      visual: visuals[0] || templateProduct.image
    });
    setMessage('Produit ajouté au panier.');
    setQuantity(1);
  };

  if (loading) {
    return <p className="cart-card__message cart-card__message--info">Chargement du produit...</p>;
  }

  if (error || !product) {
    return (
      <section className="product-detail product-detail--status">
        <p className="cart-card__message cart-card__message--error">{error || 'Produit introuvable.'}</p>
        <Link to="/boutique" className="product-detail__back">
          ← Retour à la boutique
        </Link>
      </section>
    );
  }

  return (
    <section className="product-detail">
      <div className="product-detail__gallery">
        <div className="product-detail__gallery-frame">
          <img src={currentVisual} alt={product.name} />
          {visuals.length > 1 && (
            <>
              <button
                type="button"
                className="product-detail__nav product-detail__nav--prev"
                onClick={handlePrevVisual}
                aria-label="Image précédente"
              >
                ‹
              </button>
              <button
                type="button"
                className="product-detail__nav product-detail__nav--next"
                onClick={handleNextVisual}
                aria-label="Image suivante"
              >
                ›
              </button>
              <p className="product-detail__gallery-counter">
                {safeIndex + 1} / {visuals.length}
              </p>
            </>
          )}
        </div>
      </div>
      <div className="product-detail__info">
        <p className="product-detail__collection">{product.collection || "MPA'CHI"}</p>
        <h1>{product.name}</h1>
        <p className="product-detail__price">{price} €</p>
        <p className="product-detail__description">{product.description}</p>

        <div className="product-detail__form">
          <label>
            Taille
            <select value={size} onChange={(event) => setSize(event.target.value)}>
              {availableSizes.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </label>
          <label>
            Quantité
            <input
              type="number"
              min="1"
              value={quantity}
              onChange={(event) => setQuantity(Math.max(1, Number(event.target.value)))}
            />
          </label>
          <div className="product-detail__actions">
            <button type="button" className="product-detail__primary" onClick={handleAddToCart}>
              <span aria-hidden="true" className="product-detail__primary-icon">
                <svg viewBox="0 0 24 24">
                  <path
                    fill="currentColor"
                    d="M7 7h10l1.5 12.5A1.5 1.5 0 0 1 17 21H7a1.5 1.5 0 0 1-1.5-1.5zm2-3c0-1.657 1.343-3 3-3s3 1.343 3 3v1H9zm2 0v1h2V4c0-.552-.448-1-1-1s-1 .448-1 1z"
                  />
                </svg>
              </span>
              Ajouter au panier
            </button>
          </div>
          {message && <p className="product-detail__message">{message}</p>}
        </div>

        <Link to="/boutique" className="product-detail__back">
          ← Retour à la boutique
        </Link>
      </div>
    </section>
  );
}
